import React, { useState, useEffect, useMemo } from 'react';
import './CareparrvaReportDetail.css';

/**
 * Formats values into clean percentages or readable text
 */
const formatPercent = (val) => {
  if (val === null || val === undefined || val === '' || val === 'NA' || val === 'null') {
    return '-';
  }
  const num = parseFloat(val);
  if (isNaN(num)) return String(val);
  return num.toFixed(2);
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === '' || val === 'NA' || val === 'null') {
    return '-';
  }
  return String(val);
};

/**
 * Determines text color class: negative (-) in RED, positive (+) in GREEN
 */
const getValueColorClass = (val) => {
  if (val === null || val === undefined || val === '' || val === 'NA' || val === 'null' || val === '-') {
    return 'cpo-val-neutral';
  }
  const str = String(val).trim();
  const num = parseFloat(str);
  if (!isNaN(num)) {
    if (num < 0) return 'cpo-val-negative';
    if (num > 0) return 'cpo-val-positive';
    return '';
  }
  if (str.startsWith('-')) return 'cpo-val-negative';
  if (str.startsWith('+')) return 'cpo-val-positive';
  return '';
};

/**
 * Renders formatted percent with dynamic positive green / negative red styling
 */
const renderColoredPercent = (val, forceType = null) => {
  if (val === null || val === undefined || val === '' || val === 'NA' || val === 'null') {
    return <span className="cpo-val-neutral">-</span>;
  }
  const formatted = formatPercent(val);
  if (formatted === '-') return <span className="cpo-val-neutral">-</span>;

  let cls = '';
  const num = parseFloat(val);

  if (forceType === 'positive') {
    cls = !isNaN(num) && num > 0 ? 'cpo-val-positive' : !isNaN(num) && num < 0 ? 'cpo-val-negative' : '';
  } else if (forceType === 'negative') {
    // If it's a loss / drawdown metric, positive magnitude or negative sign is negative
    cls = !isNaN(num) && num !== 0 ? 'cpo-val-negative' : '';
  } else {
    cls = getValueColorClass(val);
  }

  return <span className={cls}>{formatted}</span>;
};

/**
 * Renders value or count with dynamic positive green / negative red styling
 */
const renderColoredValue = (val, forceType = null) => {
  if (val === null || val === undefined || val === '' || val === 'NA' || val === 'null') {
    return <span className="cpo-val-neutral">-</span>;
  }
  const formatted = formatValue(val);
  if (formatted === '-') return <span className="cpo-val-neutral">-</span>;

  let cls = '';
  const num = parseFloat(val);

  if (forceType === 'positive') {
    cls = !isNaN(num) && num > 0 ? 'cpo-val-positive' : '';
  } else if (forceType === 'negative') {
    cls = !isNaN(num) && num > 0 ? 'cpo-val-negative' : '';
  } else {
    cls = getValueColorClass(val);
  }

  return <span className={cls}>{formatted}</span>;
};

const periodLabelMap = {
  '1_month': '1 Month',
  '3_months': '3 Months',
  '6_months': '6 Months',
  '1_year': '1 Year',
  '3_years': '3 Years',
  '5_years': '5 Years',
  'since_inception': 'Since Inception',
};

const formatPeriodHeader = (key) => {
  if (periodLabelMap[key]) return periodLabelMap[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const CareparrvaReportViewer = ({
  report,
  onSync,
  syncing = false,
  onDownloadJson,
}) => {
  // State for interactive single portfolio detail view
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);

  // Reset selected portfolio when report changes
  useEffect(() => {
    setSelectedPortfolio(null);
  }, [report?.id, report?.report_type]);

  // Safely parse data_points JSON
  const parsed = useMemo(() => {
    if (!report?.data_points) return null;
    if (typeof report.data_points === 'object') return report.data_points;
    try {
      return JSON.parse(report.data_points);
    } catch (e) {
      console.error('Could not parse report data points JSON:', e);
      return null;
    }
  }, [report]);

  if (!report) {
    return (
      <div className="text-center p-4 text-muted">
        No report data available.
      </div>
    );
  }

  // Handle Non-DATA_POINTS formats (PDF, PNG, JPEG, URL, QR)
  if (report.report_format && report.report_format !== 'DATA_POINTS') {
    const isImage = report.report_format === 'PNG' || report.report_format === 'JPEG';
    const isPdf = report.report_format === 'PDF';
    const isQr = report.report_format === 'QR';

    // Resolve file URL / image source (from file_url or raw_response.data.qr_code)
    let displayFileUrl = report.file_url;
    if (!displayFileUrl && report.raw_response) {
      try {
        const raw =
          typeof report.raw_response === 'string'
            ? JSON.parse(report.raw_response)
            : report.raw_response;
        const qrCode = raw?.data?.qr_code || raw?.qr_code;
        if (qrCode) {
          displayFileUrl = qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`;
        } else if (raw?.data?.url || raw?.url || raw?.data?.file_url) {
          displayFileUrl = raw?.data?.url || raw?.url || raw?.data?.file_url;
        }
      } catch (e) {
        console.error('Failed to parse raw_response for file URL:', e);
      }
    }

    return (
      <div className="p-4 bg-white border rounded">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h5 className="font-weight-bold mb-1">
              {report.report_type} Report ({report.report_format})
            </h5>
            <span className="text-muted" style={{ fontSize: '12px' }}>
              Format: <strong>{report.report_format}</strong> | Data Type: <strong>{report.data_type}</strong> | Category: <strong>{report.report_category}</strong>
            </span>
          </div>
          {displayFileUrl && (
            <div className="d-flex gap-2">
              <a
                href={displayFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-primary"
              >
                <i className="fa fa-external-link-alt mr-1"></i> Open {isQr ? 'QR Code' : 'in New Tab'}
              </a>
              <a
                href={displayFileUrl}
                download={isQr ? `careparrva_qr_${(report.report_type || 'report').toLowerCase()}.png` : true}
                className="btn btn-sm btn-success"
              >
                <i className="fa fa-download mr-1"></i> Download {isQr ? 'QR Code' : 'File'}
              </a>
            </div>
          )}
        </div>

        {displayFileUrl ? (
          <div className="mt-3">
            {isQr ? (
              <div className="text-center p-4 border rounded bg-light">
                <div className="mb-2">
                  <span
                    className="badge badge-primary px-3 py-2"
                    style={{ backgroundColor: '#0284c7', fontSize: '12px', fontWeight: 600 }}
                  >
                    <i className="fa fa-qrcode mr-1"></i> Scan QR Code to View Verification Report
                  </span>
                </div>
                <div className="d-inline-block p-3 bg-white border rounded shadow-sm my-3">
                  <img
                    src={displayFileUrl}
                    alt={`${report.report_type} QR Code`}
                    style={{ width: '280px', height: '280px', objectFit: 'contain' }}
                  />
                </div>
                <div>
                  <a
                    href={displayFileUrl}
                    download={`careparrva_qr_${(report.report_type || 'report').toLowerCase()}.png`}
                    className="btn btn-sm btn-primary font-weight-bold"
                  >
                    <i className="fa fa-download mr-1"></i> Download QR Code
                  </a>
                </div>
              </div>
            ) : isImage ? (
              <div className="text-center p-3 border rounded bg-light">
                <img
                  src={displayFileUrl}
                  alt={`${report.report_type} Report`}
                  style={{ maxWidth: '100%', maxHeight: '650px', objectFit: 'contain' }}
                />
              </div>
            ) : isPdf ? (
              <div style={{ height: '700px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                <iframe
                  src={displayFileUrl}
                  title="Report PDF"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </div>
            ) : (
              <div className="alert alert-info">
                <strong>Report URL: </strong>
                <a href={displayFileUrl} target="_blank" rel="noopener noreferrer">
                  {displayFileUrl}
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="alert alert-warning">
            No file URL available yet. If this was a historical request, it might still be generating.
            {onSync && (
              <button
                type="button"
                className="btn btn-sm btn-warning text-dark font-weight-bold ml-2"
                onClick={onSync}
                disabled={syncing}
              >
                <i className={`fa fa-sync-alt mr-1 ${syncing ? 'fa-spin' : ''}`}></i>
                {syncing ? 'Checking...' : 'Check Status'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // DATA_POINTS rendering
  if (!parsed) {
    return (
      <div className="text-center p-4 bg-white border rounded">
        <i className="fa fa-exclamation-circle text-warning fa-2x mb-2"></i>
        <h5 className="font-weight-bold">No Data Points Available</h5>
        <p className="text-muted mb-0">The data points JSON could not be found or parsed for this report.</p>
      </div>
    );
  }

  const summaryObj = parsed?.summary_data_points?.summary || {};
  const headerData =
    parsed?.detailed_data_points?.header_data_points ||
    parsed?.summary_data_points?.summary?.header_data_points ||
    parsed?.summary_data_points?.header_data_points ||
    parsed?.header_data_points ||
    null;

  // A. Equity Intraday Recommendations
  const intradaySummary = summaryObj?.equity_intraday || summaryObj?.intraday || null;

  // B. Single Stock Recommendations (Technical & Fundamental)
  const singleStockObj = summaryObj?.single_stock || summaryObj?.singlestock || {};
  const singleStockTechnical =
    singleStockObj?.single_stock_technical_recommendations ||
    (singleStockObj?.closed || singleStockObj?.open ? singleStockObj : null);
  const singleStockFundamental =
    singleStockObj?.single_stock_fundamental_recommendations || null;
  const singleStockSummary = singleStockTechnical || (singleStockObj?.closed || singleStockObj?.open ? singleStockObj : null);

  // C. Derivative Summary
  const derivativeObj = summaryObj?.derivative || summaryObj?.derivatives || {};
  const derivativeCategories =
    derivativeObj?.closed?.categories ||
    (Array.isArray(derivativeObj?.closed) ? derivativeObj.closed : null);
  const derivativeSummary = derivativeCategories || (derivativeObj?.closed ? derivativeObj : null);
  const derivativeClosedTotal = derivativeObj?.closed?.total_recommendations;
  const derivativeOpen = derivativeObj?.open || null;

  // D. Portfolio Recommendations
  const portfolioListTable = summaryObj?.portfolio_list_table || null;
  const portfolioList = portfolioListTable?.portfolios || [];
  const portfolioAggregate = portfolioListTable?.aggregate || null;

  const preferredPeriods = ['1_month', '3_months', '6_months', '1_year', '3_years', '5_years', 'since_inception'];
  const activePeriods = preferredPeriods.filter((k) =>
    portfolioList.some((p) => {
      const val = p?.return_in_percent?.[k];
      return val !== undefined && val !== null && val !== '' && val !== 'NA' && val !== 'null';
    })
  );
  const dynamicPeriods = activePeriods.length > 0 ? activePeriods : ['1_month', 'since_inception'];

  // E. Detailed Report Sections
  const detailedReport = parsed?.detailed_data_points || null;
  const detailedSectionB = detailedReport?.section_b_detailed_report || null;
  const detailedPortfolios = detailedSectionB?.portfolios || [];

  const jobId = parsed?.job_id || report?.request_id || null;
  const isPendingJob = Boolean(
    report?.status === 'PENDING' ||
      (parsed?.job_id &&
        !intradaySummary &&
        !singleStockSummary &&
        !singleStockFundamental &&
        !derivativeSummary &&
        (!portfolioList || portfolioList.length === 0) &&
        (!detailedPortfolios || detailedPortfolios.length === 0)),
  );

  const hasAnyTable = Boolean(
    intradaySummary ||
      singleStockSummary ||
      singleStockFundamental ||
      derivativeSummary ||
      (portfolioList && portfolioList.length > 0) ||
      (detailedPortfolios && detailedPortfolios.length > 0),
  );

  return (
    <div className="parrva-document-paper" id="parrva-print-area">
      {/* Header Meta info badge if available */}
      {/* {headerData && (
        <div className="p-3 mb-3 bg-light border rounded d-flex flex-wrap align-items-center justify-content-between gap-2" style={{ fontSize: '12px' }}>
          {headerData.intermediary_name && (
            <div>
              <span className="text-muted">Intermediary:</span>{' '}
              <strong>{headerData.intermediary_name}</strong>
            </div>
          )}
 
          {headerData.verification_date && (
            <div>
              <span className="text-muted">Verification Date:</span>{' '}
              <strong>{headerData.verification_date}</strong>
            </div>
          )}
          {headerData.time_session && (
            <div>
              <span className="text-muted">Session:</span>{' '}
              <strong>{headerData.time_session}</strong>
            </div>
          )}
        </div>
      )} */}

      {/* Pending Async Job Notification Banner */}
      {isPendingJob && (
        <div
          className="alert alert-warning p-4 mb-4"
          style={{ borderRadius: '6px', border: '1px solid #ffeeba' }}
        >
          <div
            className="d-flex align-items-center justify-content-between flex-wrap"
            style={{ gap: '15px' }}
          >
            <div>
              <h5 className="font-weight-bold mb-1" style={{ color: '#856404' }}>
                <i className="fa fa-clock mr-2"></i> Report Generation in Progress
              </h5>
              <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                This historical report was submitted to CareEdge PaRRVA and is currently being processed asynchronously.
                {jobId && (
                  <span>
                    {' '}
                    (Job ID: <code>{jobId}</code>)
                  </span>
                )}
              </p>
            </div>
            {onSync && (
              <button
                type="button"
                className="btn btn-warning px-3 font-weight-bold text-dark"
                onClick={onSync}
                disabled={syncing}
              >
                <i className={`fa fa-sync-alt mr-2 ${syncing ? 'fa-spin' : ''}`}></i>
                {syncing ? 'Checking CareEdge...' : 'Check Status & Sync Now'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Fallback if no tables returned and not pending */}
      {!hasAnyTable && !isPendingJob && (
        <div className="text-center p-5 border rounded bg-white">
          <i className="fa fa-info-circle fa-3x text-muted mb-3"></i>
          <h5 className="font-weight-bold mb-2">No Verification Data Points Available</h5>
          <p className="text-muted mb-3">
            No recommendation summary or detail tables were returned for this report type.
          </p>
          <div className="d-flex justify-content-center" style={{ gap: '10px' }}>
            {onSync && (
              <button
                type="button"
                className="btn btn-sm btn-warning text-dark font-weight-bold"
                onClick={onSync}
                disabled={syncing}
              >
                <i className={`fa fa-sync-alt mr-1 ${syncing ? 'fa-spin' : ''}`}></i>
                Check & Sync Status
              </button>
            )}
            {onDownloadJson && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={onDownloadJson}
              >
                Download JSON
              </button>
            )}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* 1. EQUITY INTRADAY RECOMMENDATIONS                           */}
      {/* ============================================================ */}
      {intradaySummary && (
        <div className="mb-4">
          <h2 className="parrva-section-heading">Equity Intraday Recommendations</h2>
          <table className="parrva-report-table">
            <thead>
              <tr>
                <th className="text-center">Net Avg.<br />P&L (%)</th>
                <th className="text-center">Positive<br />(Count)</th>
                <th className="text-center">Avg.<br />Profitability (%)</th>
                <th className="text-center">Negative<br />(Count)</th>
                <th className="text-center">Avg.<br />Loss (%)</th>
                <th className="text-center">Success<br />Ratio (%)</th>
                <th className="text-center">Reward to Risk<br />Ratio</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-center">{renderColoredPercent(intradaySummary.avg_net_profit_loss)}</td>
                <td className="text-center">{renderColoredValue(intradaySummary.positive, 'positive')}</td>
                <td className="text-center">{renderColoredPercent(intradaySummary.avg_profitability, 'positive')}</td>
                <td className="text-center">{renderColoredValue(intradaySummary.negative, 'negative')}</td>
                <td className="text-center">{renderColoredPercent(intradaySummary.avg_loss, 'negative')}</td>
                <td className="text-center font-weight-bold">{formatPercent(intradaySummary.success_ratio)}</td>
                <td className="text-center font-weight-bold">{formatValue(intradaySummary.reward_to_risk_ratio)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ============================================================ */}
      {/* 2. SINGLE STOCK (DELIVERY BASED)                             */}
      {/* ============================================================ */}
      {(singleStockTechnical || singleStockFundamental) && (
        <div className="mb-4">
          <h2 className="parrva-section-heading">Single Stock (Delivery based)</h2>

          {/* Technical Recommendations */}
          {singleStockTechnical && (
            <div className="mb-3">
              <h3 className="parrva-section-subheading">Technical Recommendations</h3>
              {singleStockTechnical.closed && (
                <div className="mb-3">
                  <div className="parrva-table-caption">
                    Closed Recommendations
                    {singleStockTechnical.closed.total_recommendations !== undefined && (
                      <span className="text-muted font-weight-normal ms-1">
                        (Total: {singleStockTechnical.closed.total_recommendations})
                      </span>
                    )}:
                  </div>
                  <table className="parrva-report-table">
                    <thead>
                      <tr>
                        <th className="text-center">Net Avg. P&L (%)</th>
                        <th className="text-center">Positive (Count)</th>
                        <th className="text-center">Avg. Profitability (%)</th>
                        <th className="text-center">Negative (Count)</th>
                        <th className="text-center">Avg. Loss (%)</th>
                        <th className="text-center">Success Ratio (%)</th>
                        <th className="text-center">Reward to Risk Ratio</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-center">{renderColoredPercent(singleStockTechnical.closed.avg_net_profit_loss)}</td>
                        <td className="text-center">{renderColoredValue(singleStockTechnical.closed.positive, 'positive')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockTechnical.closed.avg_profitability, 'positive')}</td>
                        <td className="text-center">{renderColoredValue(singleStockTechnical.closed.negative, 'negative')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockTechnical.closed.avg_loss, 'negative')}</td>
                        <td className="text-center font-weight-bold">{formatPercent(singleStockTechnical.closed.success_ratio)}</td>
                        <td className="text-center">{formatValue(singleStockTechnical.closed.reward_to_risk_ratio)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {singleStockTechnical.open && (
                <div className="mb-3">
                  <div className="parrva-table-caption">
                    Open Recommendations
                    {singleStockTechnical.open.total_recommendations !== undefined && (
                      <span className="text-muted font-weight-normal ms-1">
                        (Total: {singleStockTechnical.open.total_recommendations})
                      </span>
                    )}:
                  </div>
                  <table className="parrva-report-table">
                    <thead>
                      <tr>
                        <th className="text-center">Net Avg. P&L (%)</th>
                        <th className="text-center">Positive (Count)</th>
                        <th className="text-center">Unrealised Avg. Gain (%)</th>
                        <th className="text-center">Negative (Count)</th>
                        <th className="text-center">Unrealised Avg. Loss (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-center">{renderColoredPercent(singleStockTechnical.open.avg_net_profit_loss)}</td>
                        <td className="text-center">{renderColoredValue(singleStockTechnical.open.positive, 'positive')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockTechnical.open.unrealised_avg_gain, 'positive')}</td>
                        <td className="text-center">{renderColoredValue(singleStockTechnical.open.negative, 'negative')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockTechnical.open.unrealised_avg_loss, 'negative')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Fundamental Recommendations */}
          {singleStockFundamental && (
            <div className="mb-3">
              <h3 className="parrva-section-subheading">Fundamental Recommendations</h3>
              {singleStockFundamental.closed && (
                <div className="mb-3">
                  <div className="parrva-table-caption">
                    Closed Recommendations
                    {singleStockFundamental.closed.total_recommendations !== undefined && (
                      <span className="text-muted font-weight-normal ms-1">
                        (Total: {singleStockFundamental.closed.total_recommendations})
                      </span>
                    )}:
                  </div>
                  <table className="parrva-report-table">
                    <thead>
                      <tr>
                        <th className="text-center">Net Avg. P&L (%)</th>
                        <th className="text-center">Positive (Count)</th>
                        <th className="text-center">Avg. Profitability (%)</th>
                        <th className="text-center">Negative (Count)</th>
                        <th className="text-center">Avg. Loss (%)</th>
                        <th className="text-center">Success Ratio (%)</th>
                        <th className="text-center">Reward to Risk Ratio</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-center">{renderColoredPercent(singleStockFundamental.closed.avg_net_profit_loss)}</td>
                        <td className="text-center">{renderColoredValue(singleStockFundamental.closed.positive, 'positive')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockFundamental.closed.avg_profitability, 'positive')}</td>
                        <td className="text-center">{renderColoredValue(singleStockFundamental.closed.negative, 'negative')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockFundamental.closed.avg_loss, 'negative')}</td>
                        <td className="text-center font-weight-bold">{formatPercent(singleStockFundamental.closed.success_ratio)}</td>
                        <td className="text-center">{formatValue(singleStockFundamental.closed.reward_to_risk_ratio)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {singleStockFundamental.open && (
                <div className="mb-3">
                  <div className="parrva-table-caption">
                    Open Recommendations
                    {singleStockFundamental.open.total_recommendations !== undefined && (
                      <span className="text-muted font-weight-normal ms-1">
                        (Total: {singleStockFundamental.open.total_recommendations})
                      </span>
                    )}:
                  </div>
                  <table className="parrva-report-table">
                    <thead>
                      <tr>
                        <th className="text-center">Net Avg. P&L (%)</th>
                        <th className="text-center">Positive (Count)</th>
                        <th className="text-center">Unrealised Avg. Gain (%)</th>
                        <th className="text-center">Negative (Count)</th>
                        <th className="text-center">Unrealised Avg. Loss (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-center">{renderColoredPercent(singleStockFundamental.open.avg_net_profit_loss)}</td>
                        <td className="text-center">{renderColoredValue(singleStockFundamental.open.positive, 'positive')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockFundamental.open.unrealised_avg_gain, 'positive')}</td>
                        <td className="text-center">{renderColoredValue(singleStockFundamental.open.negative, 'negative')}</td>
                        <td className="text-center">{renderColoredPercent(singleStockFundamental.open.unrealised_avg_loss, 'negative')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* 3. DERIVATIVE SUMMARY REPORT                                 */}
      {/* ============================================================ */}
      {(derivativeCategories || derivativeOpen) && (
        <div className="mb-4">
          <h2 className="parrva-section-heading">Derivative Summary Report</h2>

          {/* Closed Recommendations */}
          {derivativeCategories && derivativeCategories.length > 0 && (
            <div className="mb-3">
              <div className="parrva-table-caption">
                Closed Recommendations
                {derivativeClosedTotal !== undefined && (
                  <span className="text-muted font-weight-normal ms-1">
                    (Total: {derivativeClosedTotal})
                  </span>
                )}:
              </div>
              <table className="parrva-report-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="text-center">Total (Count)</th>
                    <th className="text-center">Net Avg. P&L (%)</th>
                    <th className="text-center">Positive (Count)</th>
                    <th className="text-center">Avg. Profitability (%)</th>
                    <th className="text-center">Negative (Count)</th>
                    <th className="text-center">Avg. Loss (%)</th>
                    <th className="text-center">Success Ratio (%)</th>
                    <th className="text-center">Reward to Risk Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {derivativeCategories.map((row, rIdx) => (
                    <tr key={rIdx}>
                      <td className="font-weight-bold">{row.category}</td>
                      <td className="text-center">{row.total}</td>
                      <td className="text-center">{renderColoredPercent(row.avg_net_profit_loss)}</td>
                      <td className="text-center">{renderColoredValue(row.positive, 'positive')}</td>
                      <td className="text-center">{renderColoredPercent(row.avg_profitability, 'positive')}</td>
                      <td className="text-center">{renderColoredValue(row.negative, 'negative')}</td>
                      <td className="text-center">{renderColoredPercent(row.avg_loss, 'negative')}</td>
                      <td className="text-center font-weight-bold">{formatPercent(row.success_ratio)}</td>
                      <td className="text-center">{row.reward_to_risk_ratio}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Open Recommendations */}
          {derivativeOpen && (
            <div className="mb-3">
              <div className="parrva-table-caption">
                Open Recommendations
                {derivativeOpen.total_recommendations !== undefined && (
                  <span className="text-muted font-weight-normal ms-1">
                    (Total: {derivativeOpen.total_recommendations})
                  </span>
                )}:
              </div>
              <table className="parrva-report-table">
                <thead>
                  <tr>
                    <th className="text-center">Net Avg. P&L (%)</th>
                    <th className="text-center">Positive (Count)</th>
                    <th className="text-center">Unrealised Avg. Gain (%)</th>
                    <th className="text-center">Negative (Count)</th>
                    <th className="text-center">Unrealised Avg. Loss (%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-center">{renderColoredPercent(derivativeOpen.avg_net_profit_loss)}</td>
                    <td className="text-center">{renderColoredValue(derivativeOpen.positive, 'positive')}</td>
                    <td className="text-center">{renderColoredPercent(derivativeOpen.unrealised_avg_gain, 'positive')}</td>
                    <td className="text-center">{renderColoredValue(derivativeOpen.negative, 'negative')}</td>
                    <td className="text-center">{renderColoredPercent(derivativeOpen.unrealised_avg_loss, 'negative')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* 4. PORTFOLIO (BASKET OF SECURITIES)                          */}
      {/* ============================================================ */}
      {portfolioList.length > 0 && !selectedPortfolio && (
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <h2 className="parrva-section-heading m-0">Portfolio (Basket of securities)</h2>
          </div>

          <table className="parrva-report-table">
            <thead>
              <tr>
                <th style={{ width: '240px' }}>Portfolio Name (Click for Details)</th>
                <th className="text-center" style={{ width: '130px' }}>Date of<br />Inception on<br />PaRRVA</th>
                <th style={{ width: '220px' }}>Parameter</th>
                {dynamicPeriods.map((pKey) => (
                  <th key={pKey} className="text-center" style={{ minWidth: '100px' }}>
                    {formatPeriodHeader(pKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {portfolioList.map((item, pIdx) => {
                const retObj = item.return_in_percent || {};
                const decObj = item.max_decline_from_peak_percent || {};
                const sharpeObj = item.risk_adjusted_return_sharpe_ratio || {};

                return (
                  <React.Fragment key={pIdx}>
                    <tr>
                      <td rowSpan="3" className="font-weight-bold align-middle" style={{ verticalAlign: 'middle', backgroundColor: '#fcfdfd' }}>
                        <div
                          className="cpo-portfolio-title-link"
                          onClick={() => setSelectedPortfolio(item.portfolio_name)}
                          title="Click to view detailed strategy parameters"
                        >
                          <span>{item.portfolio_name}</span>
                        </div>
                      </td>
                      <td rowSpan="3" className="text-center align-middle" style={{ verticalAlign: 'middle', backgroundColor: '#fcfdfd', fontSize: '12px' }}>
                        {item.date_of_inception_on_parrva || '-'}
                      </td>
                      <td>Return in %</td>
                      {dynamicPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {renderColoredPercent(retObj[pKey])}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Max Decline from Peak %</td>
                      {dynamicPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {renderColoredPercent(decObj[pKey], 'negative')}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Risk Adjusted Return (Sharpe ratio)</td>
                      {dynamicPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {renderColoredPercent(sharpeObj[pKey])}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {portfolioAggregate && (
            <div className="mt-4">
              <table className="parrva-report-table">
                <thead>
                  <tr>
                    <th style={{ width: '450px' }}>Parameter</th>
                    {dynamicPeriods.map((pKey) => (
                      <th key={pKey} className="text-center" style={{ minWidth: '120px' }}>
                        {formatPeriodHeader(pKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="font-weight-bold">Range of Return in % across portfolios</td>
                    {dynamicPeriods.map((pKey) => (
                      <td key={pKey} className="text-right font-weight-bold">
                        {renderColoredValue(portfolioAggregate.return_in_percent_range?.[pKey])}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="font-weight-bold">Range of Max Decline from Peak % across portfolios</td>
                    {dynamicPeriods.map((pKey) => (
                      <td key={pKey} className="text-right font-weight-bold text-danger">
                        {renderColoredValue(portfolioAggregate.max_decline_from_peak_percent_range?.[pKey], 'negative')}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* 5. SINGLE PORTFOLIO DETAILS VIEW (ON ROW CLICK)               */}
      {/* ============================================================ */}
      {selectedPortfolio && (
        <div className="mb-4">
          {/* Top Detail Toolbar */}
          <div className="cpo-detail-toolbar">
            {/* Left side: Portfolio Name & Dropdown */}
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '16px' }}>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <i className="fa fa-briefcase text-primary" style={{ fontSize: '16px' }}></i>
                <strong style={{ fontSize: '16px', color: '#0f172a', fontWeight: 700 }}>
                  {selectedPortfolio}
                </strong>
              </div>

              {portfolioList.length > 1 && (
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>

                  <select
                    className="cpo-portfolio-select"
                    style={{ minWidth: '220px' }}
                    value={selectedPortfolio}
                    onChange={(e) => setSelectedPortfolio(e.target.value)}
                  >
                    {portfolioList.map((p, idx) => (
                      <option key={idx} value={p.portfolio_name}>
                        {p.portfolio_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Right side: Back Button */}
            <div>
              <button
                type="button"
                className="cpo-back-btn"
                onClick={() => setSelectedPortfolio(null)}
              >
                <i className="fa fa-arrow-left"></i> Back to Portfolios List
              </button>
            </div>
          </div>

          {/* Detailed Content for the Selected Portfolio */}
          {(() => {
            const activeDetail = detailedPortfolios.find(
              (dp) =>
                dp.portfolio_snapshot?.strategy_name?.trim().toLowerCase() ===
                selectedPortfolio.trim().toLowerCase()
            ) || detailedPortfolios.find(
              (dp) =>
                dp.portfolio_snapshot?.strategy_name?.toLowerCase().includes(selectedPortfolio.toLowerCase()) ||
                selectedPortfolio.toLowerCase().includes(dp.portfolio_snapshot?.strategy_name?.toLowerCase())
            );

            if (!activeDetail) {
              const pOverview = portfolioList.find((p) => p.portfolio_name === selectedPortfolio);
              const retObj = pOverview?.return_in_percent || {};
              const decObj = pOverview?.max_decline_from_peak_percent || {};
              const sharpeObj = pOverview?.risk_adjusted_return_sharpe_ratio || {};

              return (
                <div className="p-4 border rounded bg-white">
                  <h3 className="parrva-section-subheading mb-3">{selectedPortfolio} Performance</h3>
                  <table className="parrva-report-table">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        {dynamicPeriods.map((pKey) => (
                          <th key={pKey} className="text-center">{formatPeriodHeader(pKey)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Return in %</td>
                        {dynamicPeriods.map((pKey) => (
                          <td key={pKey} className="text-right">{renderColoredPercent(retObj[pKey])}</td>
                        ))}
                      </tr>
                      <tr>
                        <td>Max Decline from Peak %</td>
                        {dynamicPeriods.map((pKey) => (
                          <td key={pKey} className="text-right">{renderColoredPercent(decObj[pKey], 'negative')}</td>
                        ))}
                      </tr>
                      <tr>
                        <td>Sharpe Ratio</td>
                        {dynamicPeriods.map((pKey) => (
                          <td key={pKey} className="text-right">{renderColoredPercent(sharpeObj[pKey])}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            }

            const snap = activeDetail.portfolio_snapshot || {};
            const riskParams = activeDetail.return_risk_parameters || {};
            const highLow = activeDetail.fifty_two_week_high_low;
            const chart = activeDetail.historical_index_movement_chart?.[0];

            const preferred = ['1_month', '3_months', '6_months', '1_year', '3_years', '5_years', 'since_inception'];
            const stratPeriods = preferred.filter((k) => {
              const val = riskParams.return_in_percent?.[k];
              return val !== undefined && val !== null && val !== '' && val !== 'NA' && val !== 'null';
            });
            const activeStratPeriods = stratPeriods.length > 0 ? stratPeriods : ['1_month', 'since_inception'];

            return (
              <div className="p-3 bg-white">
                {/* Snapshot Table */}
                <h4 className="parrva-section-subheading mb-2">Strategy Snapshot</h4>
                <table className="parrva-report-table mb-4">
                  <thead>
                    <tr>
                      <th style={{ width: '50%' }}>Parameter</th>
                      <th style={{ width: '50%' }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-weight-bold">Strategy Name</td>
                      <td>{snap.strategy_name || selectedPortfolio}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Asset Class</td>
                      <td className="text-capitalize">{snap.asset_class || 'Equity'}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Date of Inception on PaRRVA</td>
                      <td>{snap.date_of_inception_on_parrva || '-'}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">No. of Stocks</td>
                      <td>{snap.no_of_stocks ?? '-'}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">No. Of Rebalances/Modifications</td>
                      <td>{snap.no_of_rebalances_modifications ?? 0}</td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Corporate Action</td>
                      <td>{snap.corporate_action || '-'}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Benchmark movement */}
                {chart && (
                  <div className="p-2 mb-3 bg-light border text-muted" style={{ fontSize: '12px' }}>
                    <strong>Historical Index Movement Benchmark:</strong> Index Value is <strong>{parseFloat(chart.index_value).toFixed(2)}</strong> as of <strong>{chart.date}</strong>.
                  </div>
                )}

                {/* Return & Risk Parameters Table */}
                <h4 className="parrva-section-subheading mb-2">Return & Risk Parameters</h4>
                <table className="parrva-report-table mb-4">
                  <thead>
                    <tr>
                      <th style={{ width: '150px' }}>Details</th>
                      <th style={{ width: '320px' }}>Parameter</th>
                      {activeStratPeriods.map((pKey) => (
                        <th key={pKey} className="text-center" style={{ minWidth: '110px' }}>
                          {formatPeriodHeader(pKey)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-weight-bold align-middle bg-light">Return Parameters</td>
                      <td>Return in %</td>
                      {activeStratPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {renderColoredPercent(riskParams.return_in_percent?.[pKey])}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td rowSpan="4" className="font-weight-bold align-middle bg-light">Risk Parameters</td>
                      <td>Max drawdown or decline from peak %</td>
                      {activeStratPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {renderColoredPercent(riskParams.max_drawdown_or_decline_from_peak_percent?.[pKey], 'negative')}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Standard deviation %</td>
                      {activeStratPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {formatPercent(riskParams.standard_deviation?.[pKey])}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>Risk Adjusted Return (Sharpe ratio)</td>
                      {activeStratPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {renderColoredPercent(riskParams.sharpe_ratio?.[pKey])}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>No. of days taken to recover from max drawdown</td>
                      {activeStratPeriods.map((pKey) => (
                        <td key={pKey} className="text-right">
                          {formatValue(riskParams.no_of_days_to_recover_from_max_drawdown?.[pKey])}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>

                {/* 52-Week High & Low Value Table */}
                {highLow && (
                  <>
                    <h4 className="parrva-section-subheading mb-2">52-Week High & Low Value</h4>
                    <table className="parrva-report-table mb-2">
                      <thead>
                        <tr>
                          <th className="text-center" style={{ width: '50%' }}>
                            52 weeks high hit as on {highLow.high_date || '-'}
                          </th>
                          <th className="text-center" style={{ width: '50%' }}>
                            52 weeks low hit as on {highLow.low_date || '-'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-center font-weight-bold text-success" style={{ fontSize: '13px' }}>
                            {highLow.high_value ? parseFloat(highLow.high_value).toFixed(2) : '-'}
                          </td>
                          <td className="text-center font-weight-bold text-danger" style={{ fontSize: '13px' }}>
                            {highLow.low_value ? parseFloat(highLow.low_value).toFixed(2) : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default CareparrvaReportViewer;

